<!-- eslint-disable vue/multi-word-component-names -->
<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts" generic="TItem">
import type { ClassValue } from 'clsx'
import { pick } from 'dot-object'
import debounce from 'lodash-es/debounce'
import find from 'lodash-es/find'
import isEmpty from 'lodash-es/isEmpty'
import orderBy from 'lodash-es/orderBy'
import { ChevronRightIcon, ViewListIcon } from 'mdi-vue3'
import { type PrimitiveProps } from 'reka-ui'
import { computed, type DefineComponent, type FunctionalComponent, type HTMLAttributes, markRaw, provide, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { type RouteLocationRaw, useRouter } from 'vue-router'

import { cn, isVisible } from '../../../lib/utils'
import { AButton, type AButtonsItemInterface, ACalloutCard, ACard, ADivider, ASkeleton, type FixedLengthArray, type Testable } from '../../../main'
import ARouterLink from '../ARouterLink/ARouterLink.vue'
import type { TableFilterOptions, TableFilterValueOptions, TableHeaderOptions, TableSortOption, TableSorts } from '.'
import { TableBody, TableCell, TableFooter, TableHead, TableHeader, TableLoader, TablePagination, TableRow, TableSearch, TableSortIcon } from '.'
import { useTableResponsive } from './composables'
import TableActiveFilters from './TableActiveFilters.vue'
import TableBase from './TableBase.vue'
import TableFilters from './TableFilters.vue'

export type TableSortEvent = {
  key: string
  header: TableHeaderOptions
  direction: TableSortOption
}

export type TableRowEvent<T> = {
  item: T
  event: MouseEvent
}

export type TableSearchEvent = {
  query: string
}

export type TablePaginationEvent = TablePaginationOptions

export interface TableEmits<T> {
  (event: 'sort', data: TableSortEvent): void
  (event: 'page', data: TablePaginationEvent): void
  (event: 'search', data: TableSearchEvent): void
  (event: 'row', data: TableRowEvent<T>): void
}

export interface TableProperties<T> extends Testable, PrimitiveProps {
  class?: HTMLAttributes['class']
  id?: HTMLAttributes['id']
  label?: string
  labelPlural?: string
  actions?: AButtonsItemInterface[]
  items: T[]
  headers: TableHeaderOptions[]
  filters?: TableFilterOptions[]
  footer?: Record<string, T>
  clickable?: boolean
  sortable?: boolean
  searchable?: boolean
  loading?: boolean
  skeleton?: boolean
  search?: boolean | string | undefined
  pagination?: TablePaginationOptions | boolean
  to?: (item: T) => RouteLocationRaw | undefined
  rowClassForItem?: string | ((item: T) => ClassValue[])
  icon?: FunctionalComponent | DefineComponent
}

export interface TablePaginationOptions {
  currentPage: number
  perPage: number
  total: number
}

const properties = withDefaults(defineProps<TableProperties<TItem>>(), {
  as: ACard,
  id: undefined,
  asChild: true,
  class: undefined,
  search: undefined,
  clickable: false,
  filters: () => [],
  footer: undefined,
  actions: () => [],
  sortable: undefined,
  searchable: true,
  loading: false,
  skeleton: false,
  pagination: undefined,
  label: undefined,
  labelPlural: undefined,
  to: undefined,
  rowClassForItem: undefined,
  icon: markRaw(ViewListIcon),
})

const { t } = useI18n({
  messages: {
    en: {
      open: 'Open {item}',
      '{item}': '{item}',
      '{item} have not been added yet': "@.capitalize:{'{item}'} have not been added yet.",
      '{item} not present': "No @.capitalize:{'{item}'}",
      zeroRecords: 'No Results',
      zeroRecordsDescription: 'No results to display.',
    },
    nl: {
      open: 'Bekijk {item}',
      '{item}': '{item}',
      '{item} have not been added yet': "Op dit moment zijn er nog geen @.lower:{'{item}'}.",
      '{item} not present': "Er zijn geen @.lower:{'{item}'}",
      zeroRecords: 'Geen resultaten',
      zeroRecordsDescription: 'Geen resultaten om weer te geven.',
    },
    de: {
      open: 'Öffne {item}',
      '{item} have not been added yet': "Es wurden noch keine @.lower:{'{item}'} hinzugefügt.",
      '{item} not present': "Es sind keine @.lower:{'{item}'} vorhanden",
      zeroRecords: 'Keine Ergebnisse',
      zeroRecordsDescription: 'Keine Ergebnisse zum Anzeigen.',
    },
  },
})

const paginationDefaults = {
  currentPage: 1,
  perPage: 15,
  total: properties.items.length || 0,
}

const { isMobile } = useTableResponsive()

const pagination = (properties.pagination === true ? paginationDefaults : properties.pagination) || paginationDefaults
const hasPagination = computed(() => properties.pagination !== false)

const emit = defineEmits<TableEmits<TItem>>()
const data = ref(properties.items)
const page = ref(pagination.currentPage)
const pageSize = ref(pagination.perPage)
const total = ref(pagination.total)

const filterValues = defineModel<Record<string, TableFilterValueOptions>>('filterValues', { required: false, default: {} })

const router = useRouter()
const sorts = ref<TableSorts>(getDefaultSorts(properties.headers))

provide('sorts', sorts)

const hasItems = computed(() => data.value.length > 0)
const search = ref<string>(properties.search === 'string' ? properties.search : '')
function getDefaultSorts(headers: TableHeaderOptions[]): TableSorts {
  const defaults: TableSorts = {}
  for (const header of headers) {
    defaults[header.key] = undefined
  }

  return defaults
}

function getHeaderForKey(key: string): TableHeaderOptions | undefined {
  return find(properties.headers, (header) => header.key === key)
}

const canSortItems = computed((): boolean => {
  return properties.sortable === undefined || properties.sortable
})

function getOrderByPayload(sorts: TableSorts): FixedLengthArray<[string[], TableSortOption[]]> {
  const payload: FixedLengthArray<[string[], TableSortOption[]]> = [[], []]

  for (const [key, direction] of Object.entries(sorts)) {
    if (direction === undefined) continue
    const header = getHeaderForKey(key)!
    payload[0].push(header.sortKey || header.key)
    payload[1].push(direction)
  }

  return payload
}

const onClickSort = debounce((key: string) => {
  if (!getHeaderForKey(key)?.sortable) return
  sorts.value[`${key}`] = getNextSortDirection(key)
  emit('sort', { key, header: getHeaderForKey(key)!, direction: sorts.value[`${key}`] })

  if (canSortItems.value) {
    const [keys, directions] = getOrderByPayload(sorts.value)

    data.value = orderBy(properties.items, keys, directions as any)
  }
}, 200)

function getNextSortDirection(key: string) {
  const current = sorts.value[`${key}`]
  if (current === undefined) return 'asc'
  if (current === 'asc') return 'desc'
  if (current === 'desc') return
}

function getRowClassForItem(item: any): ClassValue[] {
  if (typeof properties.rowClassForItem === 'string') return [properties.rowClassForItem]
  if (typeof properties.rowClassForItem === 'function') return properties.rowClassForItem(item)
  return []
}

function getValueForHeader(header: TableHeaderOptions, item: Record<string, unknown>) {
  const value = pick(header.key, item)
  return header.formatter ? header.formatter(value, item) : value
}

function onClickRow(item: TItem) {
  emit('row', item as any)
  if (!properties.clickable) return
  if (properties.to) {
    router.push(properties.to(item) as any)
  }
}

const hasOnClickRow = computed((): boolean => properties.clickable)

const isDataEmpty = computed(() => data.value.length === 0)
const visibleFilters = computed(() => (properties.filters || []).filter((filter) => (filter.visible === undefined || filter.visible) && filter.label))
const showFilters = computed((): boolean => !isEmpty(visibleFilters.value))
const showActiveFilters = computed((): boolean => !isEmpty(filterValues.value) && showFilters.value)
const showSearchOrFilters = computed((): boolean => properties.searchable || showFilters.value || showActiveFilters.value)
const visibleHeaders = computed(() => properties.headers.filter((header) => isVisible(header)))

watch(
  () => properties.items,
  () => {
    data.value = properties.items
  },
)

watch(page, () => {
  emit('page', { currentPage: page.value, perPage: pageSize.value, total: total.value })
})

watch(pageSize, () => {
  emit('page', { currentPage: page.value, perPage: pageSize.value, total: total.value })
})

watch(search, () => {
  emit('search', { query: search.value })
})

watch(
  () => properties.pagination,
  () => {
    total.value = typeof properties.pagination === 'boolean' ? 0 : properties.pagination?.total || 0
  },
)

defineOptions({ inheritAttrs: true })
</script>

<template>
  <div :class="['atabix-table-wrapper', { 'atabix-table-wrapper--mobile': isMobile }]" :data-test>
    <div v-if="showSearchOrFilters" class="flex flex-row justify-between">
      <div class="flex flex-row gap-2">
        <template v-if="showFilters || showActiveFilters">
          <TableFilters v-if="showFilters" v-model="filterValues" :filters="filters" />
          <TableActiveFilters v-if="showActiveFilters" v-model="filterValues" :filters="visibleFilters" />
        </template>
      </div>
      <template v-if="searchable && search !== undefined">
        <TableSearch v-if="!skeleton" v-model="search" />
        <ASkeleton v-if="skeleton" class="h-8 w-80" />
      </template>
    </div>

    <TableBase v-bind="$attrs" :as="as">
      <template v-if="!isDataEmpty">
        <table :class="cn('atabix-table', properties.class)">
          <TableHeader>
            <TableRow>
              <TableHead
                v-for="(header, index) in visibleHeaders"
                :key="index"
                :align="header.align"
                :class="{ 'cursor-pointer': header.sortable }"
                :data-test="`header:${header.key}`"
                :width="header.width"
                @click="onClickSort(header.key)"
              >
                {{ header.title }}
                <TableSortIcon v-if="header.sortable" class="ml-2" :direction="sorts[header.sortKey || header.key]" />
              </TableHead>
              <TableHead v-if="properties.to" class="w-14"></TableHead>
            </TableRow>
            <TableLoader v-show="loading" class="absolute bottom-0 z-10" />
          </TableHeader>
          <TableBody v-if="hasItems">
            <template v-if="!isMobile">
              <TableRow
                v-for="(item, index) in data"
                :key="index"
                :class="cn({ 'cursor-pointer': hasOnClickRow }, getRowClassForItem(item))"
                :data-test="`row-id:${item?.id}`"
                :item="item"
                @click="onClickRow(item)"
              >
                <TableCell
                  v-for="(header, i) in visibleHeaders"
                  :key="`header-${i}`"
                  :align="header.align"
                  :data-test="`cell-header:${header.key}`"
                  :truncate="header.truncate"
                  :width="header.width"
                >
                  <slot v-bind="{ value: pick(header.key, item), item, header }" :name="header.key">
                    {{ getValueForHeader(header, item) }}
                  </slot>
                </TableCell>
                <TableCell v-if="to" class="atabix-table-cell__action" :data-test="`cell-action:${to(item)?.name}`">
                  <ARouterLink :to="to(item)">
                    <ChevronRightIcon class="size-5" />
                  </ARouterLink>
                </TableCell>
              </TableRow>
            </template>
            <template v-if="isMobile">
              <template v-for="(item, index) in data" :key="index">
                <table data-test="table-screen:mobile">
                  <tr v-for="(header, i) in visibleHeaders" :key="`header-${i}`" :data-test="`row-id-mobile:${item?.id}`">
                    <td class="atabix-table-cell-mobile" :data-test="`header-mobile:${header.key}`">
                      {{ header.title }}
                    </td>
                    <td class="py-2 pl-2" :data-test="`cell-header-mobile:${header.key}`" width="70%">
                      <slot v-bind="{ value: pick(header.key, item), item, header }" :name="header.key">
                        {{ getValueForHeader(header, item) }}
                      </slot>
                    </td>
                  </tr>
                </table>
                <div v-if="to" class="atabix-table-cell__mobile-action" data-test="cell-action-screen:mobile">
                  <slot v-bind="{ item }" name="action-cell-mobile-prepend" />
                  <AButton
                    class="w-full"
                    :data-test="`cell-action:${to(item)?.name}`"
                    :icon="markRaw(ChevronRightIcon)"
                    :to="to(item)"
                    variant="outline"
                  >
                    {{ t('open', { item: label || '' }) }}
                  </AButton>
                </div>
                <ADivider v-if="index !== data.length - 1" class="my-4" />
              </template>
            </template>
          </TableBody>
          <TableFooter v-if="footer && !isMobile">
            <TableRow>
              <TableCell
                v-for="(header, i) in visibleHeaders"
                :key="`footer-${i}`"
                :align="header.align"
                :truncate="header.truncate"
                :width="header.width"
              >
                <slot v-bind="{ value: pick(header.key, footer), footer, header }" :name="`footer.${header.key}`">
                  {{ pick(header.key, footer) }}
                </slot>
              </TableCell>
              <TableCell v-if="to"></TableCell>
            </TableRow>
          </TableFooter>
        </table>
        <div v-if="hasPagination" class="border-t px-2">
          <TablePagination
            v-if="pagination"
            v-model:page="page"
            v-model:size="pageSize"
            :class="[{ 'w-full': isMobile }]"
            :sizeable="!isMobile"
            :total="total"
          />
        </div>
      </template>
      <template v-if="isDataEmpty && !skeleton" #table>
        <div class="relative">
          <TableLoader v-show="loading" class="absolute top-0 z-10" />
          <slot name="callout">
            <ACalloutCard
              :border="false"
              class="h-80"
              :description="
                properties.labelPlural ? t('{item} have not been added yet', { item: t(properties.labelPlural) }) : t('zeroRecordsDescription')
              "
              :icon="icon"
              :items="actions"
              :title="properties.labelPlural ? t('{item} not present', { item: t(properties.labelPlural) }) : t('zeroRecords')"
            />
          </slot>
        </div>
      </template>
      <template v-else-if="skeleton" #table>
        <table :class="cn('atabix-table', properties.class)">
          <TableHeader>
            <TableRow>
              <TableHead
                v-for="(header, index) in visibleHeaders"
                :key="index"
                :align="header.align"
                :class="{ 'cursor-pointer': header.sortable }"
                :width="header.width"
                @click="onClickSort(header.key)"
              >
                <ASkeleton class="h-5 w-full" />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-for="j in Array(5)" :key="j">
              <TableCell v-for="(header, i) in visibleHeaders" :key="`header-${i}`" :align="header.align" :width="header.width">
                <ASkeleton class="h-5 w-full" />
              </TableCell>
            </TableRow>
          </TableBody>
        </table>
      </template>
    </TableBase>
  </div>
</template>

<style lang="scss">
.atabix-table-wrapper {
  @apply flex flex-col gap-y-6;
}

.atabix-table-container {
  .atabix-table {
    @apply w-full;
    @apply border-spacing-0;
  }
}

.atabix-table {
}

.atabix-table-cell-mobile {
  @apply whitespace-nowrap;
  @apply py-2;
  @apply pr-2;
  @apply font-semibold;
  @apply text-table-foreground-header;
}

.atabix-table-cell__action {
  @apply w-14;
  @apply text-right;
}

.atabix-card.atabix-table-container {
  .atabix-card__content {
    @apply p-0;
  }

  th:first-child,
  td:first-child.atabix-table-cell {
    @apply pl-6;
  }

  th:last-child,
  td:last-child.atabix-table-cell {
    @apply pr-6;
  }
}
</style>
